import React, { useRef } from 'react';
import { Column } from 'primereact/column';
import { Skeleton } from 'primereact/skeleton';
import { OverlayPanel } from 'primereact/overlaypanel';

import { formatCurrency } from '../../../utils/Utils';
import {
    marketplaceStatus,
    formatMarketplaceRank,
    marketplaceLabel,
    marketplaceCurrency,
    operatorTextOptions,
    sortedMarketplaceEntries,
    ownStoreEntries,
    cheapestAndPriciestMarketplaces,
    formatDelivery,
} from './marketplaceProfile';

// Product list columns of the marketplace operator profile, placed straight in
// the DataTable children. Every value comes from the summary row's mp_* fields.
// The shared Status column gets its marketplace lines (MarketplaceStatusLines)
// and its sorting (marketplaceStatusColumnProps) from here as well.

const STATUS_CHIP = {
    leader: 'pux-chip-green',
    behind: 'pux-chip-red',
    alone: 'pux-chip-blue',
    no_price: 'pux-chip-gray',
};

const MUTED = '#9ca3af';
const SUBTLE = '#6b7280';
const LINK = '#2563eb';
const RED = '#dc2626';
const GREEN = '#16a34a';
const SMALL = { fontSize: '.78rem', color: SUBTLE, marginTop: 2 };
const dash = <span style={{ color: MUTED }}>—</span>;

const isNumber = (value) => typeof value === 'number' && Number.isFinite(value);

// Positive: the own price is above the compared price (red); zero or negative:
// level with or below it (green).
const formatPct = (value) => {
    if (!isNumber(value)) return dash;
    return <span style={{ color: value > 0 ? RED : GREEN }}>{`${value > 0 ? '+' : ''}${value.toFixed(2)}%`}</span>;
};

const money = (row, value) => (isNumber(value) ? formatCurrency(value, marketplaceCurrency(row)) : dash);

// Sorts the table rows by one numeric value, missing values last in both
// directions. getRows is read at sort time: DataTable keeps the sortFunction of
// the column last clicked and calls it again on every render, so it must never
// sort a stale copy of the rows.
const sortByValue = (valueOf, getRows) => ({ order }) => {
    const current = getRows() || [];
    return current
        .map((row, index) => {
            const value = row ? valueOf(row) : null;
            return { row, index, value: isNumber(value) ? value : null };
        })
        .sort((a, b) => {
            if (a.value == null && b.value == null) return a.index - b.index;
            if (a.value == null) return 1;
            if (b.value == null) return -1;
            return (order || 1) * (a.value - b.value) || a.index - b.index;
        })
        .map(({ row }) => row);
};

// Extra props of the shared Status column: sortable by the buybox diff % in the
// marketplace profile, nothing for any other profile.
export const marketplaceStatusColumnProps = (enabled, getRows) => (enabled
    ? { field: 'mp_diff_pct', sortable: true, sortFunction: sortByValue((row) => row.mp_diff_pct, getRows) }
    : {});

// Marketplace lines of the Status column: status chip, rank and buybox diff %.
export function MarketplaceStatusLines({ row, t }) {
    if (!row) return null;
    const status = marketplaceStatus(row);
    const rank = formatMarketplaceRank(row);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, marginBottom: 4 }}>
            <span className={`pux-chip ${STATUS_CHIP[status]}`}>{t(`product.marketplace.status_${status}`)}</span>
            {rank && <span style={{ fontSize: '.78rem', color: SUBTLE }}>{t('product.marketplace.rank')} {rank}</span>}
            {isNumber(row.mp_diff_pct) &&
                <span style={{ fontSize: '.78rem', fontWeight: 600 }} title={t('product.marketplace.diff_pct')}>{formatPct(row.mp_diff_pct)}</span>}
        </div>
    );
}

// The operator's own offer: 1P price and diff, buybox price and holder, own seller count.
const OwnOfferCell = ({ row, t, operator }) => {
    const ownCount = isNumber(row.mp_own_seller_count) ? row.mp_own_seller_count : 0;
    return (
        <div className="p-text-nowrap">
            {isNumber(row.mp_first_party_price)
                ? <div style={{ fontSize: '1.05rem', fontWeight: 600 }}>{money(row, row.mp_first_party_price)}</div>
                : <div style={{ color: MUTED }}>{t('product.marketplace.first_party_missing', operator)}</div>}
            {isNumber(row.mp_first_party_diff_pct) &&
                <div style={{ fontSize: '.72rem' }} title={t('product.marketplace.first_party_diff_pct', operator)}>
                    {formatPct(row.mp_first_party_diff_pct)}
                </div>}
            <div style={SMALL}>
                {t('product.marketplace.buybox_label')} {money(row, row.mp_buybox_price)}
                {row.mp_buybox_holder && <React.Fragment>
                    {' – '}
                    {row.mp_buybox_holder_is_first_party &&
                        <span className="pux-chip pux-chip-blue" style={{ marginRight: 4 }}>{t('product.marketplace.first_party_badge', operator)}</span>}
                    {row.mp_buybox_holder}
                </React.Fragment>}
            </div>
            <div style={SMALL}>{t('product.marketplace.n_sellers', { count: ownCount })}</div>
        </div>
    );
};

// The competing marketplaces: cheapest and most expensive, plus a link that
// opens every marketplace of the row in an overlay. The overlay also lists the operator's
// own stores on other marketplaces below the table (never counted as competitors); a row
// with only own stores shows a link to them instead of the dash.
const OtherMarketplacesCell = ({ row, t, onSellersClick, operator }) => {
    const panel = useRef(null);
    const { cheapest, priciest, total } = cheapestAndPriciestMarketplaces(row);
    const ownStores = ownStoreEntries(row);
    const stop = (e) => e.stopPropagation();

    if (total === 0 && ownStores.length === 0) return dash;

    const openSellers = (e, key) => {
        e.stopPropagation();
        if (panel.current) panel.current.hide();
        onSellersClick(row, key);
    };

    return (
        <div className="p-text-nowrap">
            {cheapest
                ? <div title={t('product.marketplace.cheapest_marketplace')}>
                    <span style={{ color: GREEN }}>▼</span> {marketplaceLabel(cheapest.key)}{' '}
                    <strong>{money(row, cheapest.min_price)}</strong>{' '}
                    <span style={{ fontSize: '.78rem', color: SUBTLE }}>({t('product.marketplace.n_sellers', { count: cheapest.count })})</span>
                </div>
                : (total > 0 ? <div>{dash}</div> : null)}
            {cheapest && formatDelivery(cheapest.delivery, t) &&
                <div style={SMALL} title={t('product.marketplace.delivery')}>
                    <i className="pi pi-truck" style={{ fontSize: '.72rem', marginRight: 4 }} />
                    {formatDelivery(cheapest.delivery, t, cheapest.delivery_is_buybox)}
                </div>}
            {priciest && cheapest && priciest.key !== cheapest.key &&
                <div style={SMALL} title={t('product.marketplace.priciest_marketplace')}>
                    <span style={{ color: RED }}>▲</span> {marketplaceLabel(priciest.key)} {money(row, priciest.min_price)}
                </div>}
            <button type="button" className="p-link preventSelect"
                    style={{ fontSize: '.75rem', color: LINK, textDecoration: 'underline', marginTop: 2 }}
                    onClick={(e) => { e.stopPropagation(); if (panel.current) panel.current.toggle(e); }}>
                {total > 0
                    ? t('product.marketplace.n_marketplaces', { count: total })
                    : t('product.marketplace.n_own_stores', { count: ownStores.length })}
            </button>
            <OverlayPanel ref={panel} appendTo={document.body} dismissable style={{ maxWidth: '95vw' }}>
                <div onClick={stop} style={{ maxHeight: '24em', overflow: 'auto' }}>
                    {total > 0 && <React.Fragment>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('product.marketplace.all_marketplaces')}</div>
                    <table style={{ borderCollapse: 'collapse', fontSize: '.85rem' }}>
                        <tbody>
                            {sortedMarketplaceEntries(row).map((entry) => (
                                <tr key={entry.key} style={{ color: entry.counted ? 'inherit' : MUTED }}>
                                    <td style={{ padding: '3px 10px 3px 0' }}>
                                        {marketplaceLabel(entry.key)}
                                        {!entry.counted &&
                                            <small title={t('product.marketplace.extra_tooltip')}> ({t('product.marketplace.extra')})</small>}
                                    </td>
                                    <td style={{ padding: '3px 10px', textAlign: 'right' }}>
                                        {entry.min_price != null && entry.url
                                            ? <a href={entry.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                                {money(row, entry.min_price)}
                                            </a>
                                            : money(row, entry.min_price)}
                                    </td>
                                    <td style={{ padding: '3px 10px' }}>{entry.seller || ''}</td>
                                    <td style={{ padding: '3px 10px', fontSize: '.78rem', color: SUBTLE }}
                                        title={t('product.marketplace.delivery')}>{formatDelivery(entry.delivery, t, entry.delivery_is_buybox) || ''}</td>
                                    <td style={{ padding: '3px 0 3px 10px' }}>
                                        {entry.count > 0 && onSellersClick
                                            ? <button type="button" className="p-link preventSelect"
                                                      style={{ fontSize: '.78rem', color: LINK, textDecoration: 'underline' }}
                                                      onClick={(e) => openSellers(e, entry.key)}>
                                                {t('product.marketplace.n_sellers', { count: entry.count })}
                                            </button>
                                            : <span style={{ fontSize: '.78rem' }}>{t('product.marketplace.n_sellers', { count: entry.count })}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </React.Fragment>}
                    {ownStores.length > 0 && <React.Fragment>
                        <div style={{ fontWeight: 600, margin: total > 0 ? '10px 0 6px' : '0 0 6px' }}>
                            {t('product.marketplace.own_stores_group', operator)}
                            <small style={{ fontWeight: 400, color: SUBTLE }}> ({t('product.marketplace.own_stores_hint')})</small>
                        </div>
                        <table style={{ borderCollapse: 'collapse', fontSize: '.85rem' }}>
                            <tbody>
                                {ownStores.map((store) => (
                                    <tr key={store.key}>
                                        <td style={{ padding: '3px 10px 3px 0' }}>{marketplaceLabel(store.key)}</td>
                                        <td style={{ padding: '3px 10px', textAlign: 'right' }}>
                                            {store.url
                                                ? <a href={store.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                                                    {money(row, store.price)}
                                                </a>
                                                : money(row, store.price)}
                                        </td>
                                        <td style={{ padding: '3px 0 3px 10px', fontSize: '.78rem', color: SUBTLE }}
                                            title={t('product.marketplace.delivery')}>{formatDelivery(store.delivery, t) || ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </React.Fragment>}
                </div>
            </OverlayPanel>
        </div>
    );
};

export const marketplaceColumns = ({ t, rows, campaign, loading, onSellersClick, getRows }) => {
    const operator = operatorTextOptions(campaign?.settings);
    const readRows = getRows || (() => rows);

    const cell = (render) => (row) => {
        if (loading || !row) return <Skeleton />;
        return render(row);
    };

    return [
        <Column key="mp_own_offer" field="mp_first_party_price" header={t('product.marketplace.own_column', operator)}
                body={cell((row) => <OwnOfferCell row={row} t={t} operator={operator} />)}
                sortable sortFunction={sortByValue((row) => row.mp_first_party_price, readRows)} />,
        <Column key="mp_other_marketplaces" field="mp_best_price" header={t('product.marketplace.other_marketplaces')}
                body={cell((row) => <OtherMarketplacesCell row={row} t={t} onSellersClick={onSellersClick} operator={operator} />)}
                sortable sortFunction={sortByValue((row) => row.mp_best_price, readRows)} />,
    ];
};

export default marketplaceColumns;
